import { createContext, useContext, useRef, useState, useCallback, useEffect } from "react";

const AudioCtx = createContext(null);

export function AudioProvider({ children }) {
  const ref = useRef(null);
  const wantPlay = useRef(false);
  const [src, setSrc] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);

  const attemptPlay = useCallback(() => {
    ref.current?.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
  }, []);

  const play = useCallback((url) => {
    if (url && url !== src) {
      // New track: swap the source, then play once React has applied the new src.
      wantPlay.current = true;
      setProgress(0);
      setSrc(url);
    } else {
      attemptPlay();
    }
  }, [src, attemptPlay]);

  // Once the <audio> element reflects a freshly-requested src, start playback.
  useEffect(() => {
    if (src && wantPlay.current) {
      wantPlay.current = false;
      attemptPlay();
    }
  }, [src, attemptPlay]);

  const toggle = useCallback(() => {
    if (!ref.current) return;
    if (playing) { ref.current.pause(); setPlaying(false); }
    else attemptPlay();
  }, [playing, attemptPlay]);

  const seek = useCallback((pct) => {
    if (ref.current && ref.current.duration) ref.current.currentTime = pct * ref.current.duration;
  }, []);

  return (
    <AudioCtx.Provider value={{ src, playing, progress, duration, play, toggle, seek }}>
      {children}
      <audio
        ref={ref}
        src={src || undefined}
        onTimeUpdate={() => {
          const el = ref.current;
          if (el && el.duration) setProgress((el.currentTime / el.duration) * 100);
        }}
        onLoadedMetadata={(e) => setDuration(e.target.duration)}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => { setPlaying(false); setProgress(0); }}
      />
    </AudioCtx.Provider>
  );
}

export function useAudio() {
  const ctx = useContext(AudioCtx);
  if (!ctx) throw new Error("useAudio must be used within AudioProvider");
  return ctx;
}
